import { fileService } from '../../clients/ApiClients';

/**
 * Object URLs for attachments, keyed by `file_id`.
 *
 * The inline endpoint sits behind `AuthMiddleware`, so an `<img src>`
 * pointing at it sends no `Authorization` header and gets a 401. The bytes
 * have to come through the authenticated axios instance as a blob, and a
 * blob has to become an object URL before an `<img>` can show it.
 *
 * **Object URLs are deliberately not revoked when a thumbnail unmounts.**
 * Switching between the internal and shared threads unmounts every
 * thumbnail on screen, and revoking on the way out leaves the cache
 * holding URLs that render as broken images when the user switches back.
 * The cache owns the lifetime instead, and releases a URL only when it
 * evicts the entry.
 */
const urls = new Map<string, string>();
const failures = new Map<string, AttachmentError>();
const inFlight = new Map<string, Promise<string>>();

/**
 * How many blobs to hold. A violation with a hundred photos should not
 * pin a hundred blobs in memory for the life of the tab; a thread with a
 * dozen should never evict.
 */
const MAX_CACHED = 40;

export type AttachmentError =
  /** Hard-deleted, or never ours to read. */
  | 'not-found'
  /** The extension is not on the inline allowlist — heic reaching here. */
  | 'not-renderable'
  | 'failed';

const classify = (err: unknown): AttachmentError => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404) return 'not-found';
  if (status === 415) return 'not-renderable';
  return 'failed';
};

const evictOldest = () => {
  while (urls.size > MAX_CACHED) {
    const oldest = urls.keys().next();
    if (oldest.done) return;
    const url = urls.get(oldest.value);
    if (url) URL.revokeObjectURL(url);
    urls.delete(oldest.value);
  }
};

export const cachedUrl = (fileId: string): string | undefined =>
  urls.get(fileId);

export const cachedFailure = (fileId: string): AttachmentError | undefined =>
  failures.get(fileId);

/**
 * Fetch an attachment's bytes and hand back an object URL.
 *
 * Concurrent callers for the same file share one request — two thumbnails
 * of the same photo, or React's double mount in development, cost one
 * round trip. A failure is cached too, so a missing file is not re-fetched
 * on every render.
 */
export const loadInlineUrl = (fileId: string): Promise<string> => {
  const existing = urls.get(fileId);
  if (existing) return Promise.resolve(existing);

  const failed = failures.get(fileId);
  if (failed) return Promise.reject(failed);

  const pending = inFlight.get(fileId);
  if (pending) return pending;

  const request = fileService
    .serveFileInline({ id: fileId }, { responseType: 'blob' as const })
    .then((response) => {
      // The route declares no response schema, so the generated method is
      // typed loosely. `responseType` is what decides how axios reads it.
      const url = URL.createObjectURL(response.data as unknown as Blob);
      urls.set(fileId, url);
      inFlight.delete(fileId);
      evictOldest();
      return url;
    })
    .catch((err) => {
      const reason = classify(err);
      failures.set(fileId, reason);
      inFlight.delete(fileId);
      throw reason;
    });

  inFlight.set(fileId, request);
  return request;
};

/** Test seam — the cache outlives any component by design. */
export const __resetAttachmentCache = (): void => {
  urls.forEach((url) => URL.revokeObjectURL(url));
  urls.clear();
  failures.clear();
  inFlight.clear();
};
